function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from "react";
import PropTypes from "prop-types";
import MUIDataTable from "mui-datatables";
import { useLocation } from "react-router-dom";
// Material imports
import makeStyles from "@mui/styles/makeStyles";
import LinearProgress from "@mui/material/LinearProgress";
import Fade from "@mui/material/Fade";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

// Limitless imports
import { NavigationContext } from "../../hooks/navigation/NavigationContext";
import { useTableStateContext } from "../../hooks/table-state/TableStateContext";
import ViewTableColumns from "./ViewTableColumns";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import useCustomTheme from "expert-web-app/src/utils/hooks/useCustomTheme";

// Custom theme to adjust background color for the table
const getMuiTheme = theme => createTheme({
  components: {
    MUIDataTable: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.elevation1,
          color: theme.palette.text.primary
        }
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.elevation1,
          color: theme.palette.text.primary,
          zIndex: 10
        }
      }
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.elevation1,
          color: theme.palette.text.primary
        }
      }
    }
  }
});
const useStyles = makeStyles({
  tableContainer: {
    paddingLeft: props => props.minWidth ? 1 : 8,
    paddingRight: props => props.minWidth ? 1 : 8,
    paddingTop: 8,
    paddingBottom: 1,
    overflow: "hidden",
    transition: "width 300ms ease"
  },
  tableBack: {
    backgroundColor: "#FF0000"
  },
  tablePaper: {
    boxShadow: "none"
  }
});
const DataTable = ({
  options,
  loading = false,
  containerStyle,
  minWidth = false,
  ...rest
}) => {
  const {
    data,
    tableId,
    columns
  } = rest;
  const {
    getOptions
  } = useTableStateContext();
  const classes = useStyles({
    minWidth
  });
  const theme = useCustomTheme();
  const tableStyle = containerStyle ? `${classes.tableContainer} ${containerStyle}` : classes.tableContainer;
  const {
    muiOptions,
    muiColumns
  } = getOptions(tableId, columns, options, data, loading);
  muiOptions.tableBodyMaxHeight = "310px";
  const CustomSearchIcon = () => /*#__PURE__*/React.createElement(SearchIcon, {
    sx: {
      color: theme.palette.primary.main
    }
  });
  const CustomFilterIcon = () => /*#__PURE__*/React.createElement(FilterListIcon, {
    sx: {
      color: theme.palette.primary.main
    }
  });
  const iconComponents = {
    icons: {
      SearchIcon: CustomSearchIcon,
      FilterIcon: CustomFilterIcon
    }
  };
  const tableComponents = {
    TableViewCol: ViewTableColumns
  };
  const components = () => {
    if (process.env.REACT_APP_NAME === "ExpertWebApp") {
      return {
        ...tableComponents,
        ...iconComponents
      };
    } else {
      return {
        tableComponents
      };
    }
  };
  const muiTable = () => /*#__PURE__*/React.createElement(MUIDataTable, _extends({}, rest, {
    options: muiOptions,
    columns: muiColumns,
    components: components()
  }));
  const location = useLocation();
  const isOnboarding = location.pathname.includes("/onboarding/detail");
  return /*#__PURE__*/React.createElement(NavigationContext.Consumer, null, () => /*#__PURE__*/React.createElement("div", {
    className: tableStyle
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      height: 5
    }
  }, /*#__PURE__*/React.createElement(Fade, {
    in: loading,
    style: {
      transitionDelay: loading ? "200ms" : "0ms"
    },
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement(LinearProgress, {
    color: "secondary"
  }))), !loading && isOnboarding ? /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: getMuiTheme(theme)
  }, muiTable()) : muiTable()));
};
DataTable.propTypes = {
  options: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  containerStyle: PropTypes.string,
  minWidth: PropTypes.bool,
  flatten: PropTypes.bool
};
export default DataTable;